import Button from 'components/Button/Button'
import { BUTTON_TYPES } from 'components/Button/constants'
import Popup from 'components/Popup/Popup'
import Select from 'components/Select/Select'
import { OPTIONS_SORT_ORDER, ROLES_NAMES } from 'constants/common'
import { BTN_TXT, PLACEHOLDERS } from 'constants/txt'
import { createRFP } from 'features/RFP/RFPDetails/api'
import useRouter from 'hooks/useRouter'
import { useEffect, useLayoutEffect, useMemo, useState } from 'react'
import { useSelector } from 'react-redux'
import { useParams } from 'react-router-dom'
import { getUser } from 'redux/store/user/getters'
import { routes } from 'router/Config/config.routes'
import {
  getContractCategoryUsersForReassign,
  getContractUsersForReassign,
  getCPRUsersForReassign,
  getPRSUsersForReassign,
  getRFPUsersForMandatoryReassign,
  getRFPUsersForReassign,
  reassignContractCategoryResponsible,
  reassignContractOwner,
  reassignCPRResponsible,
  reassignPRSOwner,
  reassignRFPOwner
} from './api'
import './styles.scss'
import { TReassignPopup as Props } from './types'
import Typography from '../../components/Typography/Typography'
import { usePRSDetailsContext } from '../PRS/PRSDetails/Providers/PRSDetailsContextProvider'
import { ROLES } from 'features/Permission/constants'
import { getRcaList, updatePRS } from '../../pages/ProductRequests/api'
import { notification } from '../../components/Notification'
import { IPartialUserInfo } from '../../redux/store/user/types'
import { useSelect } from './useSelect'

export const Reassign = ({
  isRFP,
  isReassignPopupOpen,
  setIsReassignPopupOpen,
  ownerRole,
  ownerName,
  uuid,
  update,
  isCreatingRFP,
  rfpData,
  isPrs,
  isContractCategory,
  isRca,
  isCommons,
  isCPR,
  isAddNew,
  params
}: Props) => {
  const [users, setUsers] = useState<IPartialUserInfo[]>([])
  const [filteredOptions, setFilteredOptions] = useState<IPartialUserInfo[]>([])
  const [user, setUser] = useState('')
  const prsData = usePRSDetailsContext()
  const currentUser = useSelector(getUser)
  const { id } = useParams()
  const { push } = useRouter()
  const isVendor = currentUser.role.includes('vendor')
  const isVP = currentUser.role === ROLES.VICE_PRESIDENT

  const { returnSelectUsers, handleSearch } = useSelect({
    users,
    setFilteredOptions
  })

  useLayoutEffect(() => {
    if ((uuid || id) && id !== 'new') {
      if (isRFP) {
        getRFPUsersForReassign(uuid || id).then((res) => {
          setUsers(res?.data?.results)
          setFilteredOptions(res?.data?.results)
        })
      } else if (isPrs) {
        if (isRca && id) {
          getRcaList(id).then((res) => {
            setUsers(
              res.data.results.map((i) => ({ ...i, role: ROLES.COMMUNITY_RCA }))
            )
            setFilteredOptions(
              res.data.results.map((i) => ({ ...i, role: ROLES.COMMUNITY_RCA }))
            )
          })
        } else {
          getPRSUsersForReassign(uuid || id).then((res) => {
            setUsers(res?.data?.results)
            setFilteredOptions(res?.data?.results)
          })
        }
      } else if (isCPR) {
        if (params)
          getCPRUsersForReassign({ params: params }).then((res) => {
            setUsers(res?.data?.results)
            setFilteredOptions(res?.data?.results)
          })
      } else if (isContractCategory) {
        if (!id) return
        getContractCategoryUsersForReassign(id).then((res) => {
          setUsers(res?.data?.results)
          setFilteredOptions(res?.data?.results)
        })
      } else {
        getContractUsersForReassign(uuid || id).then((res) => {
          setUsers(res?.data?.results)
          setFilteredOptions(res?.data?.results)
        })
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    if (isCreatingRFP) {
      getRFPUsersForMandatoryReassign(rfpData?.contract_categories).then(
        (res) => {
          setUsers(res?.data?.results)
          setFilteredOptions(res?.data?.results)
        }
      )
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rfpData?.contract_categories])

  const onCancel = () => {
    setIsReassignPopupOpen(false)
    setUser('')
  }

  const onConfirm = () => {
    if (isCreatingRFP) {
      createRFP({ ...rfpData, ...{ creator: user } }).then((resp) => {
        push(`${routes.bidding}${routes.rfp}/${resp?.data?.uuid}/`)
      })
      return
    }
    if (isRFP) {
      reassignRFPOwner(uuid || id || '', user).then(() => {
        if (update) {
          update()
        }
        onCancel()
      })
      return
    }
    if (isCPR) {
      reassignCPRResponsible(uuid || id || '', user).then(() => {
        if (update) {
          update()
        }
        onCancel()
      })
      return
    }
    if (isContractCategory) {
      reassignContractCategoryResponsible(uuid || id || '', user).then(() => {
        notification.success({
          message: 'Contract Category owner has been successfully reassigned.'
        })
        if (update) {
          update()
        }
        onCancel()
      })
      return
    }
    if (isPrs) {
      if (isRca && id) {
        updatePRS(id, { rca: user }).then(() => {
          notification.success({
            message: 'Revenue Cycle Advisor has been successfully reassigned.'
          })
          if (update) {
            update()
          }
          onCancel()
        })
        return
      } else {
        reassignPRSOwner(uuid || id || '', user).then(() => {
          if (update) {
            update()
          }
          onCancel()
        })
        return
      }
    }

    reassignContractOwner(uuid || id || '', user).then(() => {
      if (update) {
        update()
      }
      onCancel()
    })
  }

  const isSO = useMemo(() => {
    return (
      isContractCategory ||
      (isRFP &&
        (ownerRole === ROLES.VICE_PRESIDENT ||
          ownerRole === ROLES.CONTRACT_STEWARD ||
          ownerRole === ROLES.COMMONS_VICE_PRESIDENT ||
          ownerRole === ROLES.COMMONS_CONTRACT_STEWARD))
    )
  }, [isContractCategory, isRFP, ownerRole])

  const modalTitle = useMemo(() => {
    const role = isSO ? 'Sourcing owner' : ROLES_NAMES[ownerRole]
    return isPrs
      ? 'Reassign PRS'
      : isCPR
      ? 'Responsible for Contract pipeline'
      : `${isAddNew ? 'Add' : 'Reassign'} ${role} ${ownerName}`
  }, [isPrs, isCPR, isAddNew, ownerRole, ownerName, isSO])

  return (
    <Popup
      visible={isReassignPopupOpen}
      onCancel={onCancel}
      className="reassign-popup__container"
      title={modalTitle}
      footer={
        <>
          <Button type={BUTTON_TYPES.DEFAULT} onClick={onCancel} upperCase>
            {BTN_TXT.CANCEL}
          </Button>
          <Button
            type={BUTTON_TYPES.PRIMARY}
            onClick={onConfirm}
            disabled={!user}
            upperCase
          >
            {isCreatingRFP
              ? 'assign and create RFP'
              : isAddNew
              ? BTN_TXT.SAVE
              : BTN_TXT.REASSIGN}
          </Button>
        </>
      }
    >
      <>
        {isPrs && prsData.state?.data?.name && (
          <Typography.Body1 className="mb-10">
            If you transfer PRS to another person, you might no longer be able
            to manage &nbsp;{prsData.state?.data?.name}. Reassign PRS to the
            selected person?
          </Typography.Body1>
        )}
        <Select
          options={returnSelectUsers(filteredOptions)}
          propsItem={{
            label: `${
              isSO
                ? 'Select user'
                : isRca
                ? 'Revenue Cycle Advisor'
                : isVP && !isCPR
                ? 'Contract Steward'
                : isVendor
                ? 'Vendor Contract Steward'
                : isCommons
                ? 'OM Lifeline President / OM Lifeline Contract Steward'
                : 'Sourcing Leader / Contract Steward'
            }`,
            name: 'owner',
            required: isSO
          }}
          propsSelect={{
            filterSort: (optionA, optionB) =>
              optionA.label && optionB.label
                ? OPTIONS_SORT_ORDER[optionB.label as string] -
                  OPTIONS_SORT_ORDER[optionA.label as string]
                : 0,
            placeholder: PLACEHOLDERS.PLEASE_SELECT,
            onChange: (val) =>
              typeof val === 'string' ? setUser(val) : setUser(val?.value),
            filterOption: false,
            showSearch: true,
            labelInValue: true,
            onSearch: handleSearch
          }}
          dropdownClassName="reassign-popup__select-dropdown"
          className="reassign-popup__select"
          alwaysName
        />
      </>
    </Popup>
  )
}
